import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Film, History, Loader2, Users, LogOut } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useAuth } from '../context/AuthContext';
import BrandingFooter from '@/components/BrandingFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HomePage() {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showOptions, setShowOptions] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Subtitle options
  const [maxLength, setMaxLength] = useState(42);
  const [minDuration, setMinDuration] = useState(3);
  const [gapFrames, setGapFrames] = useState(0);
  const [lines, setLines] = useState('double');
  const [videoDuration, setVideoDuration] = useState('');
  
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      setSelectedFile(files[0]);
    }
  }, []);

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files && files[0]) {
      setSelectedFile(files[0]);
    }
  };

  const handleStartProcessing = () => {
    if (!selectedFile) {
      toast.error('Please select a video file first');
      return;
    }
    if (!videoDuration) {
      toast.error('Please select video duration range');
      return;
    }
    handleFileUpload(selectedFile);
  };

  const handleFileUpload = async (file) => {
    // Validate file type
    const validTypes = ['video/mp4', 'video/mpeg', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid video file (MP4, MOV, AVI, WEBM)');
      return;
    }

    // Validate file size (max 2GB)
    const maxSize = 2 * 1024 * 1024 * 1024; // 2GB
    if (file.size > maxSize) {
      toast.error('File size must be less than 2GB');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('video_duration_range', videoDuration);
      formData.append('max_length', maxLength.toString());
      formData.append('min_duration', minDuration.toString());
      formData.append('gap_frames', gapFrames.toString());
      formData.append('lines', lines);

      const response = await axios.post(`${API}/transcribe`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...getAuthHeaders()
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
      });

      toast.success('Transcription completed!');
      navigate(`/results/${response.data.id}`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process video');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] relative">
      <div className="hero-glow absolute top-0 left-0 right-0 h-[500px]" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header with User Info */}
        <div className="text-center mb-12 fade-in">
          <div className="flex items-center justify-between mb-6">
            <div className="flex flex-col">
              <h1 className="plotter-font text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white">
                Plotter
              </h1>
              <p className="text-[#A1A1AA]/70 text-sm font-light mt-2">
                by <a href="https://bingelabs.in/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Bingelabs</a>
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[#A1A1AA]">{user?.name}</span>
              {user?.role === 'admin' && (
                <Button
                  onClick={() => navigate('/team')}
                  className="btn-secondary"
                  data-testid="manage-team-btn"
                >
                  <Users className="w-5 h-5 mr-2" />
                  Manage Team
                </Button>
              )}
              <Button
                onClick={logout}
                className="btn-secondary"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
          <p className="text-lg sm:text-xl text-[#A1A1AA] max-w-2xl mx-auto">
            Professional subtitle generation for Hindi & English videos and audio. Get accurate SRT files with Romanized text in seconds.
          </p>
        </div>

        {/* Main Upload Area */}
        <div className="max-w-4xl mx-auto">
          <div
            data-testid="upload-dropzone"
            className={`upload-dropzone ${
              dragOver ? 'drag-over' : ''
            } rounded-xl p-12 text-center cursor-pointer glass-card transition-all duration-300 ${
              uploading ? 'pointer-events-none opacity-50' : ''
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !uploading && document.getElementById('file-input').click()}
          >
            <input
              id="file-input"
              type="file"
              accept="video/*,audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac"
              className="hidden"
              onChange={handleFileSelect}
              disabled={uploading}
              data-testid="file-input"
            />

            {uploading ? (
              <div className="space-y-4">
                <Loader2 className="w-16 h-16 text-[#007AFF] mx-auto animate-spin" />
                <div className="space-y-2">
                  <p className="text-white font-medium text-lg">Processing File...</p>
                  <div className="max-w-md mx-auto bg-[#1A1A1A] rounded-full h-3 overflow-hidden">
                    <div
                      className="bg-[#007AFF] h-full transition-all duration-300 shimmer-effect"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-[#A1A1AA] text-sm">{uploadProgress}% uploaded</p>
                </div>
              </div>
            ) : (
              <>
                <Upload className="w-20 h-20 text-[#007AFF] mx-auto mb-6" />
                <h3 className="heading-font text-2xl font-bold text-white uppercase mb-3">
                  {selectedFile ? selectedFile.name : 'Drop Your File Here'}
                </h3>
                <p className="text-[#A1A1AA] mb-6">
                  {selectedFile 
                    ? `${(selectedFile.size / (1024 * 1024)).toFixed(2)} MB` 
                    : 'Video: MP4, MOV, AVI, WEBM • Audio: MP3, WAV, M4A • Max 2GB'}
                </p>
                <Button
                  data-testid="select-file-btn"
                  className="btn-primary px-8 py-6 text-lg font-medium rounded-md text-white"
                >
                  {selectedFile ? 'Change File' : 'Select File'}
                </Button>
              </>
            )}
          </div>

          {/* Subtitle Options */}
          <div className="glass-card rounded-xl p-6 mt-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="heading-font text-xl font-bold text-white uppercase">Subtitle Options</h3>
              <button
                onClick={() => setShowOptions(!showOptions)}
                className="text-[#007AFF] text-sm hover:underline"
              >
                {showOptions ? 'Hide' : 'Show'} Options
              </button>
            </div>

            {showOptions && (
              <div className="space-y-6 slide-up">
                {/* Video Duration */}
                <div>
                  <label className="block text-sm font-medium text-[#A1A1AA] mb-2">
                    Expected Video Duration
                  </label>
                  <select
                    value={videoDuration}
                    onChange={(e) => setVideoDuration(e.target.value)}
                    className="w-full px-4 py-2 bg-[#1A1A1A] border border-[#27272A] text-white rounded-md"
                    data-testid="video-duration-select"
                  >
                    <option value="">Select duration range</option>
                    <option value="0-5">Less than 5 mins</option>
                    <option value="5-15">5-15 mins</option>
                    <option value="15-30">15-30 mins</option>
                    <option value="30-60">30-60 mins</option>
                    <option value="60-90">60-90 mins</option>
                  </select>
                </div>

                {/* Max Length */}
                <div>
                  <label className="block text-sm font-medium text-[#A1A1AA] mb-2">
                    Maximum length in characters: {maxLength}
                  </label>
                  <input
                    type="range"
                    min="20"
                    max="80"
                    value={maxLength}
                    onChange={(e) => setMaxLength(parseInt(e.target.value))}
                    className="w-full"
                    data-testid="max-length-slider"
                  />
                </div>

                {/* Min Duration */}
                <div>
                  <label className="block text-sm font-medium text-[#A1A1AA] mb-2">
                    Minimum duration in seconds: {minDuration}
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    step="0.5"
                    value={minDuration}
                    onChange={(e) => setMinDuration(parseFloat(e.target.value))}
                    className="w-full"
                    data-testid="min-duration-slider"
                  />
                </div>

                {/* Gap Frames */}
                <div>
                  <label className="block text-sm font-medium text-[#A1A1AA] mb-2">
                    Gap between captions (frames): {gapFrames}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="10"
                    value={gapFrames}
                    onChange={(e) => setGapFrames(parseInt(e.target.value))}
                    className="w-full"
                    data-testid="gap-frames-slider"
                  />
                </div>

                {/* Lines */}
                <div>
                  <label className="block text-sm font-medium text-[#A1A1AA] mb-2">
                    Lines
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="single"
                        checked={lines === 'single'}
                        onChange={(e) => setLines(e.target.value)}
                        className="mr-2"
                        data-testid="lines-single"
                      />
                      <span className="text-white">Single</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="double"
                        checked={lines === 'double'}
                        onChange={(e) => setLines(e.target.value)}
                        className="mr-2"
                        data-testid="lines-double"
                      />
                      <span className="text-white">Double</span>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Generate Subtitles Button */}
          {selectedFile && (
            <div className="mt-8 text-center">
              <Button
                onClick={handleStartProcessing}
                disabled={!videoDuration || uploading}
                className="btn-primary px-12 py-6 text-xl font-bold rounded-md"
                data-testid="generate-subtitles-btn"
              >
                {uploading ? 'Processing...' : 'Generate Subtitles'}
              </Button>
              {!videoDuration && (
                <p className="text-[#FF9500] text-sm mt-2">Please select video duration range above</p>
              )}
            </div>
          )}

          {/* Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            <div className="glass-card rounded-xl p-6 card-hover">
              <div className="w-12 h-12 bg-[#007AFF]/20 rounded-lg flex items-center justify-center mb-4">
                <Film className="w-6 h-6 text-[#007AFF]" />
              </div>
              <h3 className="heading-font text-xl font-bold text-white uppercase mb-2">Accurate Transcription</h3>
              <p className="text-[#A1A1AA] text-sm">Powered by OpenAI Whisper for precise speech recognition</p>
            </div>

            <div className="glass-card rounded-xl p-6 card-hover">
              <div className="w-12 h-12 bg-[#34C759]/20 rounded-lg flex items-center justify-center mb-4">
                <span className="text-2xl">अ</span>
              </div>
              <h3 className="heading-font text-xl font-bold text-white uppercase mb-2">Romanized Hindi</h3>
              <p className="text-[#A1A1AA] text-sm">Hindi words automatically converted to Roman script</p>
            </div>

            <div className="glass-card rounded-xl p-6 card-hover">
              <div className="w-12 h-12 bg-[#FF9500]/20 rounded-lg flex items-center justify-center mb-4">
                <History className="w-6 h-6 text-[#FF9500]" />
              </div>
              <h3 className="heading-font text-xl font-bold text-white uppercase mb-2">Instant SRT Export</h3>
              <p className="text-[#A1A1AA] text-sm">Download ready-to-use subtitle files for any editor</p>
            </div>
          </div>

          {/* History Button */}
          <div className="mt-12 text-center">
            <Button
              data-testid="view-history-btn"
              onClick={() => navigate('/history')}
              className="btn-secondary px-6 py-3 rounded-md text-white"
            >
              <History className="w-5 h-5 mr-2" />
              View History
            </Button>
          </div>
        </div>
      </div>
      <BrandingFooter />
    </div>
  );
}